import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

import Editor from 'src/common/components/Editor';
import { usePublicPageEdit } from 'src/common/components/PublicPageEditContext';
import { LanguageKeys } from 'src/common/constants/languages';
import { findPageBlockById } from 'src/helpers/pageContentUtils';
import { parseTinyMCEContent } from 'src/helpers/parseTinyMCEContent';
import { PlaceholderRenderContext } from 'src/helpers/placeholderRegistry';
import { ITextBlock } from 'src/interfaces/PageBlock';

export function resolveTextHtml(
  i18n: Record<LanguageKeys, string> | undefined,
  lang: LanguageKeys,
): string {
  const raw = i18n?.[lang] ?? i18n?.en;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && 'html' in raw) {
    return String((raw as { html?: string }).html ?? '');
  }
  return '';
}

interface Props {
  blockId: string;
  html: string;
  placeholderContext: PlaceholderRenderContext;
  shellSx?: SxProps<Theme>;
  showWhenEmpty?: boolean;
}

export default function EditableRichText({
  blockId,
  html,
  placeholderContext,
  shellSx,
  showWhenEmpty = false,
}: Props) {
  const edit = usePublicPageEdit();
  const isEditMode = edit?.isEditMode ?? false;
  const editorLang = edit?.selectedEditorLang ?? LanguageKeys.en;

  const editHtml =
    isEditMode && edit
      ? resolveTextHtml(
          (findPageBlockById(edit.document, blockId) as ITextBlock | undefined)?.i18n,
          editorLang,
        )
      : html;

  if (!editHtml.trim() && !showWhenEmpty && !isEditMode) {
    return null;
  }

  const editOutlineSx: SxProps<Theme> = isEditMode
    ? theme => ({
        outline: `2px dashed ${theme.palette.primary.main}`,
        outlineOffset: 4,
        borderRadius: 1,
      })
    : {};

  if (isEditMode && edit) {
    return (
      <Box sx={[...(Array.isArray(shellSx) ? shellSx : shellSx ? [shellSx] : []), editOutlineSx]}>
        <Editor
          value={editHtml}
          onChange={v => edit.updateTextBlock(blockId, v)}
          contentType="pageContent"
          contentId={edit.pageContentId || edit.contentKey}
          fieldName="content"
        />
      </Box>
    );
  }

  const content = parseTinyMCEContent(html, {
    currentEdition: placeholderContext.currentEdition,
    dateFormat: placeholderContext.dateFormat,
  });

  return <Box sx={shellSx}>{content}</Box>;
}
